import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, UserPlus } from 'lucide-react';
import type { TaskRequestListItem } from '../types/taskRequest';
import TaskStatusBadge from './TaskStatusBadge';
import TaskPriorityBadge from './TaskPriorityBadge';
import AssignUserDialog from './AssignUserDialog';

interface TaskListProps {
  tasks: TaskRequestListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

const TYPE_LABELS: Record<string, string> = {
  SetupNewClinic: 'Add Clinic',
  ImAddClinic: 'IM - Add Clinic',
  CreateClinicRelationship: 'Relationship',
};

export default function TaskList({
  tasks,
  totalCount,
  page,
  pageSize,
  isLoading,
  onPageChange,
}: Readonly<TaskListProps>) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const [assignTaskId, setAssignTaskId] = useState<number | null>(null);
  const assignTask = assignTaskId ? tasks.find((t) => t.id === assignTaskId) : null;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleAssigned = () => {
    setAssignTaskId(null);
    queryClient.invalidateQueries({ queryKey: ['task-requests'] });
    queryClient.invalidateQueries({ queryKey: ['task-request-counts'] });
  };

  if (isLoading) {
    return (
      <div className="task-list-loading">
        <div className="loading-spinner" />
        <p>Loading tasks...</p>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="task-list-empty">
        <p>No tasks found matching the current filters.</p>
      </div>
    );
  }

  return (
    <>
      <div className="task-list-wrapper">
        <div className="task-table-container">
          <table className="task-table">
            <thead>
              <tr>
                <th className="task-col-type">Type</th>
                <th className="task-col-title">Title</th>
                <th className="task-col-status">Status</th>
                <th className="task-col-priority">Priority</th>
                <th className="task-col-assigned">Assigned To</th>
                <th className="task-col-due">Due</th>
                <th className="task-col-created">Created</th>
                <th className="task-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr
                  key={task.id}
                  className="task-table-row task-table-row-clickable"
                  onClick={() => navigate(`/tasks/${task.id}`)}
                >
                  <td className="task-col-type">
                    {TYPE_LABELS[task.taskType] ?? task.taskTypeDisplay}
                  </td>
                  <td className="task-col-title">
                    <span className="task-table-title">{task.title}</span>
                  </td>
                  <td className="task-col-status">
                    <TaskStatusBadge status={task.status} display={task.statusDisplay} />
                  </td>
                  <td className="task-col-priority">
                    <TaskPriorityBadge priority={task.priority} display={task.priorityDisplay} />
                  </td>
                  <td className="task-col-assigned">
                    {task.assignedToUserName ?? '—'}
                  </td>
                  <td
                    className={
                      task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'Completed' && task.status !== 'Rejected'
                        ? 'task-col-due task-date-overdue'
                        : 'task-col-due'
                    }
                  >
                    {task.dueDate ? formatDate(task.dueDate) : '—'}
                  </td>
                  <td className="task-col-created">{formatDate(task.createdAt)}</td>
                  <td className="task-col-actions">
                    <div className="task-row-actions">
                      <button
                        type="button"
                        className="task-row-action-btn"
                        title="Assign"
                        aria-label="Assign task"
                        onClick={(e) => { e.stopPropagation(); setAssignTaskId(task.id); }}
                      >
                        <UserPlus size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="task-pagination">
          <span className="task-pagination-info">
            {totalCount === 0
              ? 'No tasks'
              : `Showing ${(page - 1) * pageSize + 1}\u2013${Math.min(
                  page * pageSize,
                  totalCount
                )} of ${totalCount}`}
          </span>
          <div className="task-pagination-controls">
            <button
              type="button"
              className="task-pagination-btn"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="task-pagination-page">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="task-pagination-btn"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {assignTaskId && (
        <AssignUserDialog
          taskId={assignTaskId}
          currentAssignee={assignTask?.assignedToUserName}
          onAssigned={handleAssigned}
          onCancel={() => setAssignTaskId(null)}
        />
      )}
    </>
  );
}
